import React, { useState, useEffect, useCallback } from 'react';
import { 
    PackagePlus, Trash2, Plus, ArrowLeft, 
    Calculator, AlertTriangle, CheckCircle2, X, Search, FileText, Layers, Edit2 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';

const VenteSurMesure = () => {
    const navigate = useNavigate();

    // États du formulaire de vente sur-mesure
    const [articleNom, setArticleNom] = useState('');
    const [typeArticle, setTypeArticle] = useState('PF'); // 'PF' ou 'PSF'
    const [quantiteProduite, setQuantiteProduite] = useState(1);
    const [prixVente, setPrixVente] = useState(''); 
    const [groupId, setGroupId] = useState('');    
    const [uniteId, setUniteId] = useState('');
    const [observation, setObservation] = useState('');

    // Données et sélections unifiées
    const [elementsCatalogue, setElementsCatalogue] = useState([]); // Fusion MP + FT + PSF
    const [groupesProduits, setGroupesProduits] = useState([]); 
    const [unitesList, setUnitesList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null); // Élément sélectionné
    
    // États de saisie double (Quantité <-> Montant/Coût)
    const [quantiteSaisie, setQuantiteSaisie] = useState(''); 
    const [montantSaisie, setMontantSaisie] = useState(0); 
    const [lignesConsommation, setLignesConsommation] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        onConfirm: null
    });

    const COLORS = {
        bordeaux: '#800000',
        bordeauxLight: '#fef2f2',
        anthracite: '#1e293b',
        success: '#059669',
        danger: '#dc2626',
        border: '#e2e8f0'
    };

    const s = {
        container: { display: 'flex', height: '100vh', background: 'white', overflow: 'hidden' },
        mainArea: { flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr', background: '#f8fafc', position: 'relative', overflow: 'hidden' },
        leftCol: { padding: '20px', display: 'flex', flexDirection: 'column', borderRight: `2px solid ${COLORS.border}`, overflow: 'hidden' },
        rightCol: { display: 'flex', flexDirection: 'column', background: '#f1f5f9', overflow: 'hidden' },
        headerPanier: { background: COLORS.bordeaux, color: 'white', padding: '15px 20px', fontWeight: '800', textAlign: 'center', fontSize: '13px', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
        saisieSection: { padding: '15px 20px', display: 'flex', flexDirection: 'column', background: 'white', borderBottom: `2px solid ${COLORS.border}`, gap: '12px', flexShrink: 0 },
        qtyRow: { display: 'flex', gap: '8px', alignItems: 'flex-end', width: '100%' },
        inputQty: { width: '100%', padding: '10px 6px', fontSize: '18px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.bordeaux}`, borderRadius: '8px', outline: 'none' },
        inputAmount: { width: '100%', padding: '10px 6px', fontSize: '18px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.success}`, borderRadius: '8px', outline: 'none', background: '#f0fdf4', color: COLORS.success },
        btnAjouter: { padding: '0 12px', height: '52px', background: COLORS.bordeaux, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '11px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
        listeSection: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
        table: { width: '100%', borderCollapse: 'collapse' },
        thead: { background: COLORS.anthracite, color: 'white', position: 'sticky', top: 0, zIndex: 10 },
        footer: { padding: '15px 20px', background: 'white', borderTop: `2px solid ${COLORS.border}`, display: 'flex', gap: '10px', flexShrink: 0 },
        selectedRow: { background: COLORS.bordeauxLight, borderLeft: `4px solid ${COLORS.bordeaux}` },
        toastContainer: {
            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, display: 'flex', alignItems: 'center',
            background: 'white', padding: '12px 20px', borderRadius: '15px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            border: `1px solid ${COLORS.border}`,
            minWidth: '400px', transition: 'all 0.3s ease'
        },
        confirmBtn: { background: COLORS.bordeaux, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' },
        cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }
    };

    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    const fetchData = useCallback(async () => {
        try {
            const [resMatieres, resGroups, resUnites, resFiches, resPsf] = await Promise.all([
                API.get('/sales/sur-mesure/raw-materials'),
                API.get('/articles/groups').catch(() => ({ data: [] })),
                API.get('/unites').catch(() => ({ data: [] })),
                API.get('/technical-sheets').catch(() => ({ data: [] })),
                API.get('/semi-finished-products').catch(() => ({ data: [] }))
            ]);

            const rawMatieres = resMatieres.data?.data || resMatieres.data || [];
            const listMatieres = Array.isArray(rawMatieres) ? rawMatieres.filter(m => m.is_active === 1).map(m => ({
                id: m.id,
                nom: m.nom,
                type: 'MP',
                unite: m.unite_mesure || 'U',
                stock: Number(m.stock_actuel) || 0,
                cmp: Number(m.cmp) || 0,
                raw: m
            })) : [];

            const rawFiches = resFiches.data?.data || resFiches.data || [];
            const listFiches = Array.isArray(rawFiches) ? rawFiches.map(f => {
                const items = f.items || [];
                const coutCalcule = items.reduce((acc, item) => {
                    const pu = Number(item.prix_unitaire || item.prix || item.pu || 0);
                    const qte = Number(item.quantite_requise || item.quantite || item.qte || 0);
                    return acc + (pu * qte);
                }, 0);
                const qBase = Number(f.quantite_base) > 0 ? Number(f.quantite_base) : 1;

                return {
                    id: f.id || f.ID,
                    nom: f.nom || f.NOM || f.article_nom,
                    type: 'FT',
                    unite: f.unite_mesure || f.unite || 'U',
                    stock: '-',
                    quantite_base: qBase, 
                    cmp: Number(f.cout_total || f.cmp || coutCalcule) || 0,
                    raw: f
                };
            }) : [];

            const rawPsf = resPsf.data?.data || resPsf.data || [];
            const listPsf = Array.isArray(rawPsf) ? rawPsf.map(p => ({
                id: p.id || p.ID,
                nom: p.nom || p.NOM,
                type: 'PSF',
                unite: p.unite_mesure || p.unite || 'U',
                stock: Number(p.stock_actuel) || 0,
                cmp: Number(p.cmp) || 0,
                raw: p
            })) : [];

            setElementsCatalogue([...listMatieres, ...listFiches, ...listPsf]);

            const groupsData = resGroups.data?.data || resGroups.data || [];
            setGroupesProduits(Array.isArray(groupsData) ? groupsData : []);

            const unitesData = resUnites.data?.data || resUnites.data || [];
            setUnitesList(Array.isArray(unitesData) ? unitesData : []);
        } catch (err) {
            showToast("Erreur lors du chargement des données initiales", "error");
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const coutTotalMatieres = lignesConsommation.reduce((acc, l) => acc + (l.quantite * l.cmp), 0);

    const handleQtyChange = (val) => {
        setQuantiteSaisie(val);
        const qte = parseFloat(val || 0);
        if (selectedItem && (selectedItem.type === 'MP' || selectedItem.type === 'PSF')) {
            setMontantSaisie(Math.round(qte * selectedItem.cmp));
        }
    };

    const handleAmountChange = (val) => {
        setMontantSaisie(val);
        const montant = parseFloat(val || 0);
        if (selectedItem && (selectedItem.type === 'MP' || selectedItem.type === 'PSF') && selectedItem.cmp > 0) {
            const calculQte = montant / selectedItem.cmp;
            setQuantiteSaisie(Number(calculQte.toFixed(4)));
        }
    };

    const editerLignePanier = (ligne) => {
        const catalogueItem = elementsCatalogue.find(el => el.id === ligne.matiere_id);
        
        if (catalogueItem) {
            setSelectedItem(catalogueItem);
        } else {
            setSelectedItem({
                id: ligne.matiere_id,
                nom: ligne.nom,
                type: 'MP',
                unite: ligne.unite,
                stock: ligne.stockActuel || 0,
                cmp: ligne.cmp
            });
        }
        setQuantiteSaisie(ligne.quantite);
        setMontantSaisie(Math.round(ligne.quantite * ligne.cmp));
        setLignesConsommation(lignesConsommation.filter(l => l.id !== ligne.id));
    };

    const handleSelectCatalogItem = (item) => {
        setSelectedItem(item);
        if (item.type === 'FT') {
            // Par défaut, si on charge la fiche pour 1 unité de production sur-mesure, 
            // la quantité produite reste à 1 (ou quantiteProduite saisie), mais on applique la règle voulue.
            if (!quantiteProduite || quantiteProduite <= 0) {
                setQuantiteProduite(1);
            }
        } else if (item.type === 'MP' || item.type === 'PSF') {
            setQuantiteSaisie('');
        }
    };

    // 🚀 LOGIQUE CORRIGÉE : 1 unité de production sur-mesure consomme l'intégralité des ingrédients 
    // de la fiche technique de base (ex: les quantités pour 12), multipliée par le nombre d'unités sur-mesure désirées.
    const ajouterAuPanier = () => {
        if (!selectedItem) return showToast("Sélectionnez un élément dans la liste", "error");

        if (selectedItem.type === 'MP' || selectedItem.type === 'PSF') {
            const qte = parseFloat(quantiteSaisie);
            if (isNaN(qte) || qte <= 0) return showToast("Quantité consommée invalide", "error");
            
            const existingIndex = lignesConsommation.findIndex(l => l.matiere_id === selectedItem.id);
            if (existingIndex > -1) {
                const updated = [...lignesConsommation];
                updated[existingIndex].quantite += qte;
                setLignesConsommation(updated);
            } else {
                setLignesConsommation([...lignesConsommation, {
                    id: `CONSO-${Date.now()}-${Math.random().toString(36).slice(-4)}`,
                    matiere_id: selectedItem.id,
                    nom: selectedItem.nom,
                    unite: selectedItem.unite,
                    quantite: qte,
                    cmp: selectedItem.cmp,
                    stockActuel: selectedItem.stock
                }]);
            }
            showToast(`Ajouté : ${selectedItem.nom}`);
        } else if (selectedItem.type === 'FT') {
            const ingredients = selectedItem.raw?.items || [];
            if (!Array.isArray(ingredients) || ingredients.length === 0) {
                return showToast("Cette fiche technique ne contient aucun ingrédient.", "error");
            }

            const qteProduiteSurMesure = parseFloat(quantiteProduite) || 1;
            
            // Le ratio ici multiplie directement les quantités de base de la fiche par la quantité produite sur-mesure (ex: 1 unité sur-mesure = 1 fois la recette de base complète)
            let nouvellesLignes = [...lignesConsommation];
            ingredients.forEach(ing => {
                const mpId = ing.matiere_id || ing.composant_semi_fini_id || ing.id || ing.article_id;
                const mpNom = ing.designation || ing.nom || ing.matiere_nom || ing.article_nom;
                const mpUnite = ing.unite || ing.unite_mesure || 'U';
                
                const qteBaseFiche = Number(ing.quantite_requise || ing.quantite || ing.quantite_necessaire) || 0;
                
                // Quantité réelle consommée = Quantité de la fiche de base x Quantité produite sur-mesure
                const qteReelleCalculee = qteBaseFiche * qteProduiteSurMesure;

                const mpStock = Number(ing.stock_actuel) || 0;
                const mpCmp = Number(ing.prix_unitaire || ing.cmp || ing.cout_unitaire) || 0;

                const existing = nouvellesLignes.find(l => l.matiere_id === mpId);
                if (existing) {
                    existing.quantite += qteReelleCalculee;
                } else {
                    nouvellesLignes.push({
                        id: `CONSO-${Date.now()}-${Math.random().toString(36).slice(-4)}`,
                        matiere_id: mpId,
                        nom: mpNom,
                        unite: mpUnite,
                        quantite: qteReelleCalculee,
                        cmp: mpCmp,
                        stockActuel: mpStock
                    });
                }
            });
            setLignesConsommation(nouvellesLignes);
            showToast(`Fiche "${selectedItem.nom}" intégrée pour ${qteProduiteSurMesure} unité(s) sur-mesure !`);
        }

        setSelectedItem(null);
        setQuantiteSaisie('');
        setMontantSaisie(0);
    };

    const executerEnregistrementVente = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                article_nom: articleNom.trim(),
                type_article: typeArticle, 
                group_id: typeArticle === 'PF' ? groupId : null,
                unite_id: uniteId,
                quantite_produite: parseFloat(quantiteProduite),
                quantite_base: 1,
                mode_saisie: 'GLOBAL',
                prix_vente: parseFloat(prixVente),
                matieres_consommees: lignesConsommation.map(l => ({
                    matiere_id: l.matiere_id,
                    quantite_consommee: l.quantite
                })),
                observation: observation.trim()
            };
            const response = await API.post('/sales/sur-mesure', payload);
            if (response.data.success || response.data.id_production) {
                showToast("Vente sur-mesure et production enregistrées avec succès !", "success");
                setArticleNom('');
                setQuantiteProduite(1);
                setPrixVente('');
                setGroupId('');
                setUniteId('');
                setObservation('');
                setLignesConsommation([]);
                setSelectedItem(null);
                fetchData(); 
            }
        } catch (err) {
            const errMsg = err.response?.data?.error || "Erreur lors de l'enregistrement de la vente sur-mesure";
            showToast(errMsg, "error");
        } finally {
            setIsSubmitting(false);
            setNotification({ isOpen: false });
        }
    };

    const handleSubmitVente = () => {
        if (!articleNom.trim()) return showToast("Le nom du produit sur-mesure est requis", "error");
        if (!uniteId) return showToast("Veuillez sélectionner l'unité de mesure", "error");
        if (typeArticle === 'PF' && !groupId) return showToast("Veuillez sélectionner un groupe d'articles", "error");
        
        const qteProd = parseFloat(quantiteProduite);
        if (isNaN(qteProd) || qteProd <= 0) return showToast("Quantité produite invalide", "error");
        const prixVenteNum = parseFloat(prixVente);
        if (isNaN(prixVenteNum) || prixVenteNum < 0) return showToast("Le prix de vente unitaire est requis et doit être valide", "error");
        if (lignesConsommation.length === 0) return showToast("Ajoutez au moins une matière première consommée", "error");

        const composantsManquants = lignesConsommation.filter(l => l.quantite > l.stockActuel);
        if (composantsManquants.length > 0) {
            showConfirm(
                `⚠️ Stock insuffisant pour ${composantsManquants.length} composant(s). Le stock dispo sera puisé à 0 et le manque basculé en régularisation d'attente. Continuer ?`,
                () => executerEnregistrementVente()
            );
        } else {
            showConfirm(
                "Valider et enregistrer cette vente sur-mesure avec déduction des stocks ?",
                () => executerEnregistrementVente()
            );
        }
    };

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? COLORS.danger : (isConfirm ? '#f59e0b' : COLORS.success);
        return (
            <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '35px', height: '35px', borderRadius: '50%', background: `${color}15`, marginRight: '15px', flexShrink: 0 }}>
                    {notification.type === 'error' ? <X size={20} color={color} /> : isConfirm ? <AlertTriangle size={20} color={color} /> : <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: COLORS.anthracite, fontSize: '14px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => { notification.onConfirm(); }} style={s.confirmBtn}>CONFIRMER</button>
                            <button onClick={() => setNotification({isOpen: false})} style={s.cancelBtn}>ANNULER</button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}><X size={20} /></button>
                )}
            </div>
        );
    };

    return (
        <div style={s.container}>
            <Sidebar />
            <main style={s.mainArea}>
                <NotificationToast />
                
                {/* COLONNE GAUCHE */}
                <section style={s.leftCol}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px', flexShrink: 0 }}>
                        <button onClick={() => navigate(-1)} style={{ background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '50%', cursor: 'pointer' }}><ArrowLeft size={18}/></button>
                        <div>
                            <h1 style={{ fontSize: '16px', fontWeight: '900', margin: 0, color: COLORS.anthracite }}>VENTE SUR-MESURE / AD-HOC</h1>
                            <p style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>PRODUCTION DIRECTE ET TRAÇABILITÉ DES MATIÈRES</p>
                        </div>
                    </div>

                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: 2 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>NOM DU PRODUIT / ARTICLE *</label>
                                <input 
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '800' }} 
                                    placeholder="Ex: Gâteau personnalisé..." 
                                    value={articleNom}
                                    onChange={(e) => setArticleNom(e.target.value)}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>DESTINATION *</label>
                                <select 
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '800', background: 'white' }}
                                    value={typeArticle}
                                    onChange={(e) => setTypeArticle(e.target.value)}
                                >
                                    <option value="PF">Produit Fini</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            {typeArticle === 'PF' && (
                                <div style={{ flex: 1.5 }}>
                                    <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.bordeaux }}>GROUPE D'ARTICLES *</label>
                                    <select 
                                        style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: `2px solid ${COLORS.bordeaux}`, fontWeight: '800', background: 'white' }}
                                        value={groupId}
                                        onChange={(e) => setGroupId(e.target.value)}
                                    >
                                        <option value="">-- Choisir un groupe --</option>
                                        {groupesProduits.map(g => (
                                            <option key={g.id || g.ID} value={g.id || g.ID}>{g.nom || g.NOM}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div style={{ flex: 1.5 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.bordeaux }}>UNITÉ DE MESURE *</label>
                                <select 
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: `2px solid ${COLORS.bordeaux}`, fontWeight: '800', background: 'white' }}
                                    value={uniteId}
                                    onChange={(e) => setUniteId(e.target.value)}
                                >
                                    <option value="">-- Choisir une unité --</option>
                                    {unitesList.map(u => (
                                        <option key={u.id || u.ID} value={u.id || u.ID}>{u.libelle || u.LIBELLE} ({u.code || u.CODE})</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>QTÉ PRODUITE *</label>
                                <input 
                                    type="number" step="0.001" min="0.001"
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '800', textAlign: 'center' }} 
                                    value={quantiteProduite}
                                    onChange={(e) => setQuantiteProduite(e.target.value)}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.success }}>PRIX DE VENTE *</label>
                                <input 
                                    type="number" step="1" min="0"
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: `2px solid ${COLORS.success}`, fontWeight: '800', textAlign: 'center' }} 
                                    placeholder="Ex: 5000"
                                    value={prixVente}
                                    onChange={(e) => setPrixVente(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '10px', flexShrink: 0 }}>
                        <Search style={{ position: 'absolute', right: '12px', top: '10px', color: '#94a3b8' }} size={18} />
                        <input 
                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '600' }} 
                            placeholder="Rechercher une matière première, un PSF ou une fiche technique..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Tableau unifié avec les types MP, FT et PSF */}
                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white' }}>
                        <table style={s.table}>
                            <thead style={s.thead}>
                                <tr>
                                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px' }}>ÉLÉMENT CATALOGUE</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>TYPE</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>STOCK / BASE</th>
                                    <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px' }}>CMP / COÛT</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {elementsCatalogue
                                    .filter(item => item.nom.toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map(item => (
                                        <tr 
                                            key={item.id} 
                                            onClick={() => handleSelectCatalogItem(item)} 
                                            style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', ...(selectedItem?.id === item.id ? s.selectedRow : {}) }}
                                        >
                                            <td style={{ padding: '10px' }}>
                                                <div style={{ fontWeight: '800', fontSize: '12px', color: COLORS.anthracite }}>{item.nom}</div>
                                                <div style={{ fontSize: '10px', color: '#64748b' }}>Unité : {item.unite}</div>
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                <span style={{ 
                                                    padding: '3px 8px', borderRadius: '4px', fontSize: '9px', fontWeight: '900',
                                                    background: item.type === 'MP' ? '#e0f2fe' : (item.type === 'FT' ? '#f3e8ff' : '#fef3c7'),
                                                    color: item.type === 'MP' ? '#0369a1' : (item.type === 'FT' ? '#7e22ce' : '#d97706')
                                                }}>
                                                    {item.type === 'MP' ? 'MATIÈRE PREMIÈRE' : (item.type === 'FT' ? 'FICHE TECHNIQUE' : 'PRODUIT SEMI-FINI')}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: '700', color: item.stock === '-' ? '#7e22ce' : (item.stock <= 0 ? COLORS.danger : COLORS.success), fontSize: '12px' }}>
                                                {item.stock !== '-' ? `${item.stock} ${item.unite}` : `Base: ${item.quantite_base}`}
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: COLORS.anthracite, fontSize: '13px' }}>
                                                {Math.round(item.cmp).toLocaleString()} FCFA
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                {item.type === 'FT' ? <FileText size={16} color={selectedItem?.id === item.id ? COLORS.bordeaux : '#cbd5e1'}/> : <PackagePlus size={16} color={selectedItem?.id === item.id ? COLORS.bordeaux : '#cbd5e1'}/>}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* COLONNE DROITE : Panier de consommation & Validation */}
                <section style={s.rightCol}>
                    <div style={s.headerPanier}>
                        <span>Traçabilité des Matières Consommées</span>
                        <span style={{ color: '#fbbf24', fontSize: '14px' }}>{Math.round(coutTotalMatieres).toLocaleString()} FCFA</span>
                    </div>

                    <div style={s.saisieSection}>
                        <div style={{ padding: '10px 12px', background: selectedItem ? COLORS.bordeauxLight : '#f1f5f9', borderRadius: '6px', border: selectedItem ? `2.5px solid ${COLORS.bordeaux}` : '2.5px dashed #cbd5e1', margin: '0 5px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '900', color: COLORS.bordeaux }}>ÉLÉMENT SÉLECTIONNÉ :</span>
                            <div style={{ fontSize: '13px', fontWeight: '900', color: COLORS.anthracite, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedItem ? `${selectedItem.nom} (${selectedItem.type})` : "AUCUN ÉLÉMENT SÉLECTIONNÉ"}</span>
                                {selectedItem && <span style={{ fontSize: '10px', color: '#64748b', flexShrink: 0, marginLeft: '8px' }}>{selectedItem.type === 'FT' ? `Rendement Base: ${selectedItem.quantite_base}` : `Stock: ${selectedItem.stock}`}</span>}
                            </div>
                        </div>

                        {selectedItem?.type === 'MP' || selectedItem?.type === 'PSF' ? (
                            <div style={s.qtyRow}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>QTÉ ({selectedItem?.unite || '---'}) *</label>
                                    <input 
                                        type="number" 
                                        style={s.inputQty} 
                                        value={quantiteSaisie} 
                                        onChange={(e) => handleQtyChange(e.target.value)} 
                                        min="0.0001" 
                                        step="0.0001" 
                                        placeholder="0"
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.success }}>COÛT (FCFA)</label>
                                    <input 
                                        type="number" 
                                        style={s.inputAmount} 
                                        value={montantSaisie} 
                                        onChange={(e) => handleAmountChange(e.target.value)} 
                                        min="0"
                                        step="1"
                                    />
                                </div>
                                <button 
                                    onClick={ajouterAuPanier} 
                                    style={{ ...s.btnAjouter, opacity: selectedItem ? 1 : 0.5, cursor: selectedItem ? 'pointer' : 'not-allowed' }}
                                    disabled={!selectedItem}
                                >
                                    <Plus size={16}/><span>AJOUTER</span>
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button 
                                    onClick={ajouterAuPanier} 
                                    style={{ flex: 1, height: '48px', background: COLORS.bordeaux, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: selectedItem ? 1 : 0.5 }}
                                    disabled={!selectedItem}
                                >
                                    <FileText size={16}/><span>INTÉGRER LA FICHE (POUR {quantiteProduite} UNITÉS)</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <div style={s.listeSection}>
                        <table style={s.table}>
                            <thead style={{ background: COLORS.bordeaux, color: 'white', position: 'sticky', top: 0, zIndex: 5 }}>
                                <tr>
                                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px' }}>Matière Première / PSF Consommé</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>Qté Consommée</th>
                                    <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px' }}>Coût Total</th>
                                    <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignesConsommation.map((ligne) => (
                                    <tr key={ligne.id} style={{ borderBottom: '1px solid #cbd5e1', background: 'white' }}>
                                        <td style={{ padding: '10px' }}>
                                            <div style={{ fontWeight: '800', fontSize: '12px' }}>{ligne.nom}</div>
                                            <div style={{ fontSize: '10px', color: '#64748b' }}>CMP : {Math.round(ligne.cmp).toLocaleString()} / {ligne.unite}</div>
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: '900', fontSize: '12px', color: COLORS.anthracite }}>
                                            {Number(ligne.quantite.toFixed(4))} {ligne.unite}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: COLORS.success, fontSize: '13px' }}>
                                            {Math.round(ligne.quantite * ligne.cmp).toLocaleString()} FCFA
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <button 
                                                    onClick={() => editerLignePanier(ligne)}
                                                    title="Modifier"
                                                    style={{ background: '#f1f5f9', color: COLORS.anthracite, border: 'none', padding: '6px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button 
                                                    onClick={() => setLignesConsommation(lignesConsommation.filter(l => l.id !== ligne.id))} 
                                                    title="Supprimer"
                                                    style={{ color: '#ef4444', border: 'none', background: '#fee2e2', padding: '6px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {lignesConsommation.length === 0 && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', padding: '30px' }}>
                                <Calculator size={40} strokeWidth={1} />
                                <p style={{ fontWeight: '800', fontSize: '11px', marginTop: '8px' }}>AUCUN ÉLÉMENT ASSOCIÉ (SÉLECTIONNEZ UNE MP, UN PSF OU UNE FICHE)</p>
                            </div>
                        )}
                    </div>

                    <div style={{ padding: '10px 20px', background: 'white', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
                        <input 
                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                            placeholder="Observations ou notes sur la commande client (ex: sans mayonnaise)..."
                            value={observation}
                            onChange={(e) => setObservation(e.target.value)}
                        />
                    </div>

                    <div style={s.footer}>
                        <button 
                            style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', fontSize: '12px' }} 
                            onClick={() => showConfirm("Voulez-vous tout réinitialiser ?", () => { setLignesConsommation([]); setArticleNom(''); setPrixVente(''); setUniteId(''); setGroupId(''); setSelectedItem(null); })}
                        >
                            RÉINITIALISER
                        </button>
                        <button 
                            onClick={handleSubmitVente} 
                            disabled={isSubmitting || lignesConsommation.length === 0}
                            style={{ 
                                flex: 1, background: COLORS.success, color: 'white', border: 'none', padding: '12px', borderRadius: '8px', 
                                fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', 
                                opacity: (isSubmitting || lignesConsommation.length === 0) ? 0.6 : 1, fontSize: '13px' 
                            }}
                        >
                            <PackagePlus size={18} /> {isSubmitting ? "ENREGISTREMENT..." : "VALIDER LA VENTE SUR-MESURE"}
                        </button>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default VenteSurMesure;